import { useState, type MouseEvent } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EyeIcon,
  HistoryIcon,
  Loader2Icon,
  PencilIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useChatExperience } from '../chat-experience';
import { useChatToolInteraction } from '../chat-tool-interaction';
import { buildCodegenChatDocument } from '../codegen-document';
import { getChatErrorMessage } from '../chat-errors';
import { useGeneratedDocumentVersions } from '../hooks/useDocumentGeneration';
import { getDocumentGeneration } from '../services/document-generation.api';

const PAGE_SIZE = 10;

function formatVersionDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

async function downloadVersion(input: {
  conversationId: string;
  generationId: string;
}) {
  const status = await getDocumentGeneration(input.conversationId, input.generationId);
  if (status.status !== 'succeeded') {
    throw new Error('document_version_unavailable');
  }
  const response = await fetch(status.result.downloadUrl);
  if (!response.ok) {
    throw new Error('download_failed');
  }
  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = status.result.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

type ChatDocumentVersionsMenuProps = {
  conversationId: string;
  generatedDocumentId: string;
  title: string;
};

export function ChatDocumentVersionsMenu({
  conversationId,
  generatedDocumentId,
  title,
}: ChatDocumentVersionsMenuProps) {
  const { openDocument } = useChatExperience();
  const { beginDocumentRevision } = useChatToolInteraction();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pendingGenerationId, setPendingGenerationId] = useState<string | null>(null);
  const versionsQuery = useGeneratedDocumentVersions({
    conversationId,
    documentId: generatedDocumentId,
    enabled: open,
    page,
    pageSize: PAGE_SIZE,
  });
  async function handleOpenVersion(generationId: string) {
    setPendingGenerationId(generationId);
    try {
      const status = await getDocumentGeneration(conversationId, generationId);
      if (status.status !== 'succeeded') {
        throw new Error('document_version_unavailable');
      }
      openDocument(buildCodegenChatDocument({ result: status.result, title }));
      setOpen(false);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingGenerationId(null);
    }
  }

  async function handleDownloadVersion(generationId: string) {
    setPendingGenerationId(generationId);
    try {
      await downloadVersion({ conversationId, generationId });
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingGenerationId(null);
    }
  }

  function stopCardClick(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
  }

  return (
    <Popover
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setPage(1);
        }
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          aria-label={`Afficher les versions de ${title}`}
          className="shrink-0"
          onClick={stopCardClick}
          size="sm"
          type="button"
          variant="outline"
        >
          <HistoryIcon className="size-3.5" data-icon="inline-start" />
          Versions
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] p-3" onClick={stopCardClick}>
        <div className="mb-3">
          <p className="text-sm font-semibold">Historique des versions</p>
          <p className="text-xs text-muted-foreground">Seule la dernière version réussie peut être modifiée.</p>
        </div>

        {versionsQuery.isPending ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Chargement des versions
          </div>
        ) : versionsQuery.isError ? (
          <div className="py-4 text-sm text-destructive">
            {getChatErrorMessage(versionsQuery.error)}
          </div>
        ) : versionsQuery.data?.items.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground">Aucune version disponible.</div>
        ) : (
          <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {versionsQuery.data?.items.map((version) => {
              const isLatestSucceeded = version.generationId === versionsQuery.data?.latestSucceededGenerationId;
              const isPending = pendingGenerationId === version.generationId;
              return (
                <div className="rounded-lg border border-border/70 p-2.5" key={version.generationId}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">Version {version.revisionNumber}</p>
                        <Badge variant="outline">{version.status}</Badge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{formatVersionDate(version.createdAt)}</p>
                      {version.revisionInstruction ? (
                        <p className="mt-1 line-clamp-2 text-xs text-foreground/80">{version.revisionInstruction}</p>
                      ) : null}
                    </div>
                    {isPending ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button
                      disabled={version.status !== 'succeeded' || Boolean(pendingGenerationId)}
                      onClick={() => void handleOpenVersion(version.generationId)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      <EyeIcon className="size-3" data-icon="inline-start" />
                      Ouvrir
                    </Button>
                    <Button
                      disabled={version.status !== 'succeeded' || Boolean(pendingGenerationId)}
                      onClick={() => void handleDownloadVersion(version.generationId)}
                      size="xs"
                      type="button"
                      variant="outline"
                    >
                      <DownloadIcon className="size-3" data-icon="inline-start" />
                      Télécharger
                    </Button>
                    {isLatestSucceeded ? (
                      <Button
                        disabled={Boolean(pendingGenerationId)}
                        onClick={() => {
                          beginDocumentRevision({
                            baseGenerationId: version.generationId,
                            generatedDocumentId,
                            title,
                            version: version.revisionNumber,
                          });
                          setOpen(false);
                        }}
                        size="xs"
                        type="button"
                        variant="outline"
                      >
                        <PencilIcon className="size-3" data-icon="inline-start" />
                        Modifier cette version
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(versionsQuery.data?.meta.pageCount ?? 0) > 1 ? (
          <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2">
            <Button
              aria-label="Page précédente"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <ChevronLeftIcon aria-hidden />
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} sur {versionsQuery.data?.meta.pageCount}
            </span>
            <Button
              aria-label="Page suivante"
              disabled={page >= (versionsQuery.data?.meta.pageCount ?? 1)}
              onClick={() => setPage((current) => current + 1)}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <ChevronRightIcon aria-hidden />
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
